import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';

import { SortableHandle } from 'react-sortable-hoc';

import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import FormikField from '@components/form/FormikField';
import { setOpenHouseValidationResult, deleteOpenHouseValidationResult } from '@redux/modules/formGeneration';

const openHouseRules = window.FlexRule.RuntimeEngine.fromJavaScript('OnePlatform', 'OpenHouseRules');

const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical mr-3" style={{ cursor: 'move' }} />);

const OpenHouseCard = ({
  file,
  sortIndex,
  fieldsList,
}) => {
  const dispatch = useDispatch();
  const { values, setValues } = useFormikContext();
  const value = values?.OpenHouses[file.uid];

  // run after first render to hydrate default validation errors.
  useEffect(() => {
    const validateValue = value;
    const res = openHouseRules.run(validateValue);
    const { fieldValidationResult } = res.context.variableContainer; // results coming back.
    dispatch(setOpenHouseValidationResult(`${file.uid}`, fieldValidationResult)); // does set the validation result in redux according to name.
  }, []);

  const validateOpenHouseField = (FieldName, newValue) => {
    if (!value.IsDeleted) {
      const validateValue = value;
      validateValue[FieldName] = newValue;
      const res = openHouseRules.run(validateValue);
      const { fieldValidationResult } = res.context.variableContainer; // results coming back.
      dispatch(setOpenHouseValidationResult(`${file.uid}`, fieldValidationResult)); // does set the validation result in redux according to name.
    }
  };

  const handleDelete = () => {
    // update deleted entry
    const updated = {
      ...values.OpenHouses,
      [file.uid]: {
        ...file,
        IsDeleted: true,
      },
    };
    // recalculate sort order
    let index = 0;
    Object.entries(updated).forEach(([k]) => {
      if (!updated[k].IsDeleted) {
        updated[k].SortOrder = index + 1;
        index += 1;
      }
    });
    const newValues = values;
    newValues.OpenHouses = updated;
    // set formik state
    setValues(newValues);
    dispatch(deleteOpenHouseValidationResult(file.uid));
  };

  return (
    <>
      <Card id={file.uid} className="mb-g">
        <Card.Header className="pr-3 d-flex align-items-center flex-wrap">
          <div className="card-title">
            <DragHandle />
            {sortIndex + 1}
          </div>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-delete-openHouse-${file.uid}`} style={{ margin: 0 }}>Delete Open House</Tooltip>}
          >
            <Button
              name="btnDeleteOpenHouse"
              onClick={handleDelete}
              className="ml-auto"
              size="sm"
            >
              <i className="far fa-trash-alt" aria-hidden="true" />
            </Button>
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          {fieldsList && fieldsList.map(field => (
            <FormikField
              key={field.RowID}
              validateFigField={validateOpenHouseField}
              {...field}
              value={value[field.FieldName]}
              name={field.FieldName}
              FieldName={`OpenHouses.[${file.uid}].${field.FieldName}`}
              repeatableType="OpenHouse"
              repeatableId={file.uid}
            />
          ))}
        </Card.Body>
      </Card>
    </>
  );
};

OpenHouseCard.propTypes = {
  file: PropTypes.shape(),
  sortIndex: PropTypes.number.isRequired,
  validateFigField: PropTypes.func,
  fieldsList: PropTypes.arrayOf(PropTypes.shape()),
};

export default OpenHouseCard;
